/**
 * MinionEntity - Summoned minion that follows the player and auto-attacks enemies
 * 3 types: melee (Savaşçı), ranged (Okçu), caster (Büyücü)
 * Mana reserve: flat (not %), so mana-stacking = more minions
 * Dies → auto respawn after 5s at player position
 */

import { Renderer } from '@client/core/Renderer';
import { Camera } from '@client/core/Camera';
import { ElementType, Vector2 } from '@shared/types';
import { CustomSkillV2, SkillCreatorV2 } from '@shared/skills/SkillCreatorV2';
import { MinionType, ELEMENT_INFO } from '@shared/skills/SkillDeliveryTypes';
import { Player } from './Player';
import { Enemy } from './Enemy';

// ========================
// Types
// ========================

export type MinionState = 'follow' | 'chase' | 'attack' | 'dead';

export interface MinionAttack {
  targetId: string;
  targetPos: Vector2;
  damage: number;
  element: ElementType;
  isAOE: boolean;       // caster type does AOE
  aoeRadius: number;
}

// ========================
// Config per minion type
// ========================

interface MinionTypeConfig {
  attackRange: number;
  attacksPerSecond: number;
  preferredDistance: number; // ranged/caster try to keep this distance
  speed: number;
  size: number;
  icon: string;
}

const MINION_TYPE_CONFIGS: Record<MinionType, MinionTypeConfig> = {
  melee: {
    attackRange: 50,
    attacksPerSecond: 1.0,
    preferredDistance: 0,
    speed: 160,
    size: 14,
    icon: 'sword',
  },
  ranged: {
    attackRange: 200,
    attacksPerSecond: 0.8,
    preferredDistance: 150,
    speed: 140,
    size: 12,
    icon: 'bow',
  },
  caster: {
    attackRange: 250,
    attacksPerSecond: 0.6,
    preferredDistance: 200,
    speed: 120,
    size: 13,
    icon: 'orb',
  },
};

const RESPAWN_TIME = 5.0; // seconds
const FOLLOW_DISTANCE = 100; // stay within this of player
const AGGRO_RANGE = 300; // detection range for enemies
const WANDER_RADIUS = 30; // random offset when following player

// ========================
// MinionEntity
// ========================

export class MinionEntity {
  public readonly id: string;
  public readonly skillId: string;
  public position: Vector2;
  public velocity: Vector2 = { x: 0, y: 0 };
  public isAlive: boolean = true;
  public isDying: boolean = false;

  // Stats
  private hp: number;
  private maxHp: number;
  private damage: number;
  private manaReserve: number;
  private attackRange: number;
  private attacksPerSecond: number;
  private preferredDistance: number;
  private speed: number;

  // Identity
  public minionType: MinionType;
  public element: ElementType;
  private color: string;
  private secondaryColor: string;
  public size: number;

  // State
  public state: MinionState = 'follow';
  private target: Enemy | null = null;
  private attackCooldown: number = 0;
  private respawnTimer: number = 0;
  private deathFade: number = 1.0; // 1=opaque, 0=invisible (death anim)

  // Owner
  public owner: Player;

  // Pending attack for GameWorld to process
  private pendingAttack: MinionAttack | null = null;

  // Follow wander offset (so multiple minions don't stack)
  private wanderOffset: Vector2;
  private wanderTimer: number = 0;

  // Visual
  private pulseTime: number = 0;
  private attackAnimation: number = 0;

  constructor(owner: Player, skill: CustomSkillV2, position: Vector2) {
    this.id = `minion_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
    this.skillId = skill.id;
    this.owner = owner;
    this.position = { ...position };

    // Read minion type
    this.minionType = skill.minionType || 'melee';
    this.element = skill.element;

    // Read stats from skill sliders
    this.maxHp = SkillCreatorV2.getMinionHealth(skill);
    this.hp = this.maxHp;
    this.damage = SkillCreatorV2.getMinionDamage(skill);
    this.manaReserve = SkillCreatorV2.getMinionManaReserve(skill);

    // Type-specific config
    const typeConfig = MINION_TYPE_CONFIGS[this.minionType];
    this.attackRange = typeConfig.attackRange;
    this.attacksPerSecond = typeConfig.attacksPerSecond;
    this.preferredDistance = typeConfig.preferredDistance;
    this.speed = typeConfig.speed;
    this.size = typeConfig.size;

    // Colors from element
    const elementInfo = ELEMENT_INFO[this.element];
    this.color = elementInfo.color;
    this.secondaryColor = this.getSecondaryColor(this.element);

    // Random wander offset so multiple minions spread out
    this.wanderOffset = {
      x: (Math.random() - 0.5) * WANDER_RADIUS * 2,
      y: (Math.random() - 0.5) * WANDER_RADIUS * 2,
    };
  }

  private getSecondaryColor(element: ElementType): string {
    const colorMap: Record<ElementType, string> = {
      physical: '#c0c0c0',
      od: '#ffcc00',
      gok: '#88ddff',
      su: '#80d0ff',
      karanlik: '#d0a0ff',
    };
    return colorMap[element];
  }

  // ========================
  // Getters
  // ========================

  getHp(): number { return this.hp; }
  getMaxHp(): number { return this.maxHp; }
  getManaReserve(): number { return this.manaReserve; }
  getDamage(): number { return this.damage; }
  getElement(): ElementType { return this.element; }

  getHealthPercent(): number {
    return this.maxHp > 0 ? this.hp / this.maxHp : 0;
  }

  isRespawning(): boolean {
    return !this.isAlive && this.respawnTimer > 0;
  }

  getRespawnProgress(): number {
    return this.respawnTimer > 0 ? 1 - (this.respawnTimer / RESPAWN_TIME) : 1;
  }

  /**
   * Get and clear pending attack for GameWorld to process
   */
  getPendingAttack(): MinionAttack | null {
    const attack = this.pendingAttack;
    this.pendingAttack = null;
    return attack;
  }

  // ========================
  // Damage
  // ========================

  takeDamage(amount: number): number {
    if (!this.isAlive) return 0;
    const actual = Math.min(this.hp, Math.max(0, amount));
    this.hp -= actual;
    if (this.hp <= 0) {
      this.hp = 0;
      this.die();
    }
    return actual;
  }

  private die(): void {
    this.isAlive = false;
    this.isDying = true;
    this.deathFade = 1.0;
    this.state = 'dead';
    this.target = null;
    this.respawnTimer = RESPAWN_TIME;
  }

  private respawn(): void {
    this.isAlive = true;
    this.isDying = false;
    this.deathFade = 1.0;
    this.hp = this.maxHp;
    this.state = 'follow';
    this.target = null;
    this.attackCooldown = 0;
    // Spawn at player position
    this.position.x = this.owner.position.x + (Math.random() - 0.5) * 60;
    this.position.y = this.owner.position.y + (Math.random() - 0.5) * 60;
  }

  /**
   * Permanently kill (when skill is toggled off)
   */
  destroy(): void {
    this.isAlive = false;
    this.isDying = false;
    this.respawnTimer = 0;
    this.state = 'dead';
  }

  // ========================
  // Update
  // ========================

  update(deltaTime: number, enemies: Enemy[]): void {
    this.pulseTime += deltaTime;

    // Death animation
    if (this.isDying) {
      this.deathFade -= deltaTime * 2; // fade out over 0.5s
      if (this.deathFade <= 0) {
        this.deathFade = 0;
        this.isDying = false;
      }
      return;
    }

    // Dead + respawning
    if (!this.isAlive) {
      if (this.respawnTimer > 0) {
        this.respawnTimer -= deltaTime;
        if (this.respawnTimer <= 0) {
          this.respawnTimer = 0;
          this.respawn();
        }
      }
      return;
    }

    // Attack cooldown
    if (this.attackCooldown > 0) {
      this.attackCooldown -= deltaTime;
    }

    // Attack animation
    if (this.attackAnimation > 0) {
      this.attackAnimation -= deltaTime;
    }

    // Wander offset timer (change offset every 3-5s)
    this.wanderTimer -= deltaTime;
    if (this.wanderTimer <= 0) {
      this.wanderTimer = 3 + Math.random() * 2;
      this.wanderOffset.x = (Math.random() - 0.5) * WANDER_RADIUS * 2;
      this.wanderOffset.y = (Math.random() - 0.5) * WANDER_RADIUS * 2;
    }

    // Find target if needed
    if (!this.target || !this.target.isAlive || this.target.isDying) {
      this.target = this.findNearestEnemy(enemies);
    }

    // Also re-check target range (maybe it walked away far)
    if (this.target) {
      const dx = this.target.position.x - this.position.x;
      const dy = this.target.position.y - this.position.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > AGGRO_RANGE * 1.5) {
        this.target = null;
      }
    }

    if (this.target && this.target.isAlive && !this.target.isDying) {
      this.updateCombat(deltaTime);
    } else {
      this.state = 'follow';
      this.updateFollow(deltaTime);
    }
  }

  private findNearestEnemy(enemies: Enemy[]): Enemy | null {
    let nearest: Enemy | null = null;
    let nearestDist = AGGRO_RANGE;

    for (const enemy of enemies) {
      if (!enemy.isAlive || enemy.isDying) continue;
      const dx = enemy.position.x - this.position.x;
      const dy = enemy.position.y - this.position.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = enemy;
      }
    }

    return nearest;
  }

  private updateCombat(deltaTime: number): void {
    const target = this.target!;
    const dx = target.position.x - this.position.x;
    const dy = target.position.y - this.position.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (this.minionType === 'melee') {
      // Melee: run to enemy, attack in range
      if (dist <= this.attackRange) {
        this.state = 'attack';
        this.velocity.x = 0;
        this.velocity.y = 0;
        this.tryAttack(target);
      } else {
        this.state = 'chase';
        this.moveToward(target.position.x, target.position.y, deltaTime);
      }
    } else {
      // Ranged/caster: maintain preferred distance
      if (dist <= this.attackRange) {
        if (dist < this.preferredDistance * 0.7) {
          // Too close, back away
          this.state = 'chase';
          this.moveAway(target.position.x, target.position.y, deltaTime);
        } else {
          // Good distance, attack
          this.state = 'attack';
          this.velocity.x = 0;
          this.velocity.y = 0;
          this.tryAttack(target);
        }
      } else {
        // Out of range, move closer
        this.state = 'chase';
        this.moveToward(target.position.x, target.position.y, deltaTime);
      }
    }
  }

  private updateFollow(deltaTime: number): void {
    const targetX = this.owner.position.x + this.wanderOffset.x;
    const targetY = this.owner.position.y + this.wanderOffset.y;
    const dx = targetX - this.position.x;
    const dy = targetY - this.position.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist > FOLLOW_DISTANCE) {
      this.moveToward(targetX, targetY, deltaTime);
    } else if (dist > 20) {
      // Slow drift to target position
      const moveSpeed = this.speed * 0.3;
      this.velocity.x = (dx / dist) * moveSpeed;
      this.velocity.y = (dy / dist) * moveSpeed;
      this.position.x += this.velocity.x * deltaTime;
      this.position.y += this.velocity.y * deltaTime;
    } else {
      this.velocity.x = 0;
      this.velocity.y = 0;
    }

    // Teleport if too far from player (>600px)
    const playerDx = this.owner.position.x - this.position.x;
    const playerDy = this.owner.position.y - this.position.y;
    const playerDist = Math.sqrt(playerDx * playerDx + playerDy * playerDy);
    if (playerDist > 600) {
      this.position.x = this.owner.position.x + (Math.random() - 0.5) * 60;
      this.position.y = this.owner.position.y + (Math.random() - 0.5) * 60;
    }
  }

  private moveToward(tx: number, ty: number, deltaTime: number): void {
    const dx = tx - this.position.x;
    const dy = ty - this.position.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < 1) return;

    this.velocity.x = (dx / dist) * this.speed;
    this.velocity.y = (dy / dist) * this.speed;
    this.position.x += this.velocity.x * deltaTime;
    this.position.y += this.velocity.y * deltaTime;
  }

  private moveAway(tx: number, ty: number, deltaTime: number): void {
    const dx = this.position.x - tx;
    const dy = this.position.y - ty;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < 1) return;

    const retreatSpeed = this.speed * 0.6;
    this.velocity.x = (dx / dist) * retreatSpeed;
    this.velocity.y = (dy / dist) * retreatSpeed;
    this.position.x += this.velocity.x * deltaTime;
    this.position.y += this.velocity.y * deltaTime;
  }

  private tryAttack(target: Enemy): void {
    if (this.attackCooldown > 0) return;

    const baseCooldown = 1.0 / this.attacksPerSecond;
    this.attackCooldown = baseCooldown;
    this.attackAnimation = 0.25;

    const isAOE = this.minionType === 'caster';
    const aoeRadius = isAOE ? 60 : 0;

    this.pendingAttack = {
      targetId: target.id,
      targetPos: { x: target.position.x, y: target.position.y },
      damage: this.damage,
      element: this.element,
      isAOE,
      aoeRadius,
    };
  }

  // ========================
  // Render
  // ========================

  render(renderer: Renderer, camera: Camera): void {
    // Render ghost when respawning
    if (!this.isAlive && !this.isDying && this.respawnTimer > 0) {
      this.renderGhost(renderer, camera);
      return;
    }

    // Render death fade
    if (this.isDying) {
      this.renderBody(renderer, camera, this.deathFade);
      return;
    }

    if (!this.isAlive) return;

    this.renderBody(renderer, camera, 1.0);
  }

  private renderBody(renderer: Renderer, camera: Camera, alpha: number): void {
    const ctx = renderer.context;
    const screenPos = camera.worldToScreen(this.position.x, this.position.y);
    const z = camera.zoom;
    const s = this.size * z;

    ctx.save();
    ctx.globalAlpha = alpha;

    // Shadow
    ctx.fillStyle = 'rgba(0, 0, 0, 0.25)';
    ctx.beginPath();
    ctx.ellipse(screenPos.x, screenPos.y + s * 0.8, s * 0.8, s * 0.3, 0, 0, Math.PI * 2);
    ctx.fill();

    // Body - main circle
    const bodyY = screenPos.y - s * 0.3;
    const attackBounce = this.attackAnimation > 0 ? Math.sin(this.attackAnimation * Math.PI / 0.25) * 3 * z : 0;

    ctx.fillStyle = this.color;
    ctx.beginPath();
    ctx.arc(screenPos.x, bodyY - attackBounce, s, 0, Math.PI * 2);
    ctx.fill();

    // Inner glow
    ctx.fillStyle = this.secondaryColor;
    ctx.beginPath();
    ctx.arc(screenPos.x, bodyY - attackBounce, s * 0.6, 0, Math.PI * 2);
    ctx.fill();

    // Type indicator
    this.renderTypeIndicator(ctx, screenPos.x, bodyY - attackBounce, s, z);

    // Outline
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.lineWidth = 1 * z;
    ctx.beginPath();
    ctx.arc(screenPos.x, bodyY - attackBounce, s, 0, Math.PI * 2);
    ctx.stroke();

    // Health bar (only when damaged)
    if (this.hp < this.maxHp) {
      this.renderHealthBar(ctx, screenPos.x, bodyY - s - 8 * z, z);
    }

    // State indicator (subtle)
    if (this.state === 'attack' && this.attackAnimation > 0) {
      // Attack flash
      ctx.fillStyle = `rgba(255, 255, 255, ${this.attackAnimation * 2})`;
      ctx.beginPath();
      ctx.arc(screenPos.x, bodyY - attackBounce, s * 1.3, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  private renderTypeIndicator(ctx: CanvasRenderingContext2D, x: number, y: number, s: number, z: number): void {
    ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
    ctx.font = `${Math.max(8, Math.floor(s * 0.9))}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    switch (this.minionType) {
      case 'melee':
        // Sword cross
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.lineWidth = 1.5 * z;
        ctx.beginPath();
        ctx.moveTo(x - s * 0.4, y - s * 0.4);
        ctx.lineTo(x + s * 0.4, y + s * 0.4);
        ctx.moveTo(x + s * 0.4, y - s * 0.4);
        ctx.lineTo(x - s * 0.4, y + s * 0.4);
        ctx.stroke();
        break;
      case 'ranged':
        // Arrow/triangle
        ctx.beginPath();
        ctx.moveTo(x, y - s * 0.5);
        ctx.lineTo(x - s * 0.35, y + s * 0.3);
        ctx.lineTo(x + s * 0.35, y + s * 0.3);
        ctx.closePath();
        ctx.fill();
        break;
      case 'caster':
        // Small circle (orb)
        ctx.beginPath();
        ctx.arc(x, y, s * 0.3, 0, Math.PI * 2);
        ctx.fill();
        // Outer ring
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)';
        ctx.lineWidth = 1 * z;
        ctx.beginPath();
        ctx.arc(x, y, s * 0.5, 0, Math.PI * 2);
        ctx.stroke();
        break;
    }
  }

  private renderHealthBar(ctx: CanvasRenderingContext2D, x: number, y: number, z: number): void {
    const barWidth = 24 * z;
    const barHeight = 3 * z;
    const barX = x - barWidth / 2;

    // Background
    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
    ctx.fillRect(barX, y, barWidth, barHeight);

    // Fill
    const percent = this.getHealthPercent();
    const fillColor = percent > 0.5 ? '#4caf50' : percent > 0.25 ? '#ff9800' : '#f44336';
    ctx.fillStyle = fillColor;
    ctx.fillRect(barX, y, barWidth * percent, barHeight);

    // Border
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.lineWidth = 0.5;
    ctx.strokeRect(barX, y, barWidth, barHeight);
  }

  private renderGhost(renderer: Renderer, camera: Camera): void {
    const ctx = renderer.context;
    const ownerScreen = camera.worldToScreen(this.owner.position.x, this.owner.position.y);
    const z = camera.zoom;
    const s = this.size * z;

    ctx.save();

    // Pulsing ghost near player
    const progress = this.getRespawnProgress();
    const ghostAlpha = 0.15 + progress * 0.2;
    const pulse = Math.sin(this.pulseTime * 4) * 0.05;

    ctx.globalAlpha = ghostAlpha + pulse;

    // Ghost floats slightly
    const floatY = Math.sin(this.pulseTime * 2) * 4 * z;
    const ghostX = ownerScreen.x + this.wanderOffset.x * z * 0.5;
    const ghostY = ownerScreen.y + this.wanderOffset.y * z * 0.5 - s * 0.3 + floatY;

    // Ghost body
    ctx.fillStyle = this.color;
    ctx.beginPath();
    ctx.arc(ghostX, ghostY, s * 0.8, 0, Math.PI * 2);
    ctx.fill();

    // Respawn progress ring
    ctx.strokeStyle = this.secondaryColor;
    ctx.lineWidth = 2 * z;
    ctx.beginPath();
    ctx.arc(ghostX, ghostY, s * 1.2, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * progress);
    ctx.stroke();

    ctx.restore();
  }
}

// ========================
// Factory
// ========================

export function createMinionEntity(owner: Player, skill: CustomSkillV2, position: Vector2): MinionEntity {
  return new MinionEntity(owner, skill, position);
}
